/**
 * Client-side form validation for spam/abuse prevention
 * Mirrors backend validation for fast feedback
 */

const HTML_REGEX = /<[^>]+>/gi;
const SCRIPT_REGEX = /(javascript:|on\w+=|<script|<iframe|<object|<embed|<form|<input|<link|<style|<meta)/gi;

const DISPOSABLE_DOMAINS = new Set([
  'mailinator.com', 'guerrillamail.com', 'tempmail.com', 'throwaway.email',
  'yopmail.com', 'sharklasers.com', 'dispostable.com', 'mailnesia.com',
  'maildrop.cc', 'fakeinbox.com', 'trashmail.com', 'getnada.com',
  'temp-mail.org', 'emailondeck.com', '10minutemail.com', 'mohmal.com',
  'mailsac.com', 'burnermail.io', 'spamgourmet.com', 'tmpmail.net',
  'spam4.me', 'example.com', 'test.com', 'fake.com', 'notreal.com'
]);

const SPAM_NAMES = new Set(['test', 'asdf', 'aaa', 'bbb', 'xxx', 'zzz', 'qwerty', 'admin', 'null', 'undefined', 'none']);

export function containsHtml(text) {
  return HTML_REGEX.test(text) || SCRIPT_REGEX.test(text);
}

export function validateName(name) {
  if (!name || name.trim().length < 2) return 'Please enter your full name';
  if (!/^[a-zA-Z\s.'\-]+$/.test(name.trim())) return 'Name contains invalid characters';
  if (SPAM_NAMES.has(name.trim().toLowerCase().replace(/\s/g, ''))) return 'Please enter your real name';
  if (containsHtml(name)) return 'Invalid characters in name';
  return null;
}

export function validateEmail(email) {
  if (!email || !email.includes('@')) return 'Please enter a valid email';
  const [local, domain] = email.split('@');
  if (!domain || !domain.includes('.') || domain.split('.').pop().length < 2) return 'Invalid email domain';
  if (local.length < 2) return 'Invalid email address';
  if (DISPOSABLE_DOMAINS.has(domain.toLowerCase())) return 'Please use a permanent email, not a temporary one';
  if (containsHtml(email)) return 'Invalid email address';
  return null;
}

export function validatePhone(phone) {
  if (!phone) return 'Phone number is required';
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 10 || digits.length > 11) return 'Please enter a valid 10-digit phone number';
  if (digits.length === 11 && !digits.startsWith('1')) return 'Please enter a valid US phone number';
  const core = digits.slice(-10);
  if (core[0] === '0' || core[0] === '1') return 'Please enter a valid US phone number';
  if (new Set(core.split('')).size === 1) return 'Please enter a real phone number';
  if (core === '1234567890' || core === '0987654321') return 'Please enter a real phone number';
  return null;
}

export function validateTextField(text, fieldName) {
  if (!text) return null;
  if (containsHtml(text)) return `HTML code is not allowed in the ${fieldName} field`;
  return null;
}

export function validateMessage(msg) {
  if (!msg || msg.trim().length < 5) return 'Please describe your project (at least 5 characters)';
  if (containsHtml(msg)) return 'HTML code is not allowed in the message';
  const urls = (msg.match(/https?:\/\/\S+/gi) || []);
  if (urls.length > 3) return 'Too many URLs in your message';
  return null;
}

/**
 * Validate entire form. Returns { valid: boolean, errors: {field: message} }
 */
export function validateEstimateForm(data) {
  const errors = {};

  const nameErr = validateName(data.name);
  if (nameErr) errors.name = nameErr;

  const emailErr = validateEmail(data.email);
  if (emailErr) errors.email = emailErr;

  const phoneErr = validatePhone(data.phone);
  if (phoneErr) errors.phone = phoneErr;

  const addrErr = validateTextField(data.address, 'address');
  if (addrErr) errors.address = addrErr;

  const cityErr = validateTextField(data.city, 'city');
  if (cityErr) errors.city = cityErr;

  const msgErr = validateMessage(data.message);
  if (msgErr) errors.message = msgErr;

  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
}
